import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { LoadingService } from './services/init/loading-service';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import Swal from 'sweetalert2';
import { UserService } from './services/user/user.service';
import { ApplicationConfigService } from './services/application-config.service';
import { BaseComponent } from '@nucleus/ui-base';
import { AuthenticationService } from './services/authentication.service';
import { CustomIcons } from '@nucleus/ui-common';


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.css',
    standalone: false
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {

  appConfigService : ApplicationConfigService = inject(ApplicationConfigService);
  userService: UserService = inject(UserService);
  authenticationService : AuthenticationService = inject(AuthenticationService);

  // Eagerly construct so the constructor registers svg icons (csv/pdf/xls)
  // before any menu first opens. Otherwise registration is lazy and
  // mat-icon[svgIcon] renders empty until a list page is visited. (TASK-123)
  private customIcons: CustomIcons = inject(CustomIcons);

  title = 'Task Sense - Test';
  _loading : boolean = true;
  subs: Subscription = new Subscription();
  idleState : string = 'No longer idle.';
  timedOut: boolean = false;

  private idleInitialized = false;

  constructor(private loadingService: LoadingService, private idle : Idle) {
    super();
  }

  ngOnInit(): void {
    this.subs = this.loadingService.loading.subscribe(b => this.onLoadingEvent(b));
  }

  private onLoadingEvent(value : boolean) {
    this._loading = value;
    // Initialize idle watch ONCE on the first load→ready transition. Without
    // this guard, every subsequent loading cycle (e.g. SSE reconnects) would
    // re-subscribe the handlers, stacking listeners over time.
    if (value == false && !this.idleInitialized) {
      this.idleInitialized = true;
      let sessionIdle = localStorage.getItem('session.idle');
      let sessionTimeout = localStorage.getItem('session.timeout');
  
      if (sessionIdle != null  && sessionIdle != undefined) {
        this.idle.setIdle(+sessionIdle); // in seconds
      } else {
        this.idle.setIdle(60); // in seconds
      }
  
      if (sessionTimeout != null  && sessionTimeout != undefined) {
        this.idle.setTimeout(+sessionTimeout); // in seconds
      } else {
        this.idle.setTimeout(15); // in seconds
      }
  
      this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
  
      // Define responses to idle events
      this.idle.onIdleEnd.subscribe(() => this.handleIdleEnd());
      this.idle.onTimeout.subscribe(() => this.handleTimeout());
      this.idle.onIdleStart.subscribe(() => this.handleIdleStart());
      this.idle.onTimeoutWarning.subscribe((countdown) => this.handleTimeoutWarning(countdown));
     
      this.idle.watch();      
    }
  }

  private handleIdleEnd() : void {
    if (this.timedOut == false) {
      this.idleState = 'No longer idle.';
      this.stay();
      Swal.close();
    }
  }

  private handleTimeout() : void {
    this.idle.stop();
    this.idleInitialized = false;
    Swal.close();

    // Idle window expired. Delegate the entire logout + re-auth to the BFF.
    // The SPA cannot reach the HttpOnly session cookies, and restarting the
    // OAuth flow is a server-side concern. The BFF /oauth2/reauthenticate
    // endpoint revokes the token, clears the access/refresh/session cookies
    // (including the auth-server JSESSIONID) so re-auth begins from a clean,
    // unauthenticated state, then bounces into the OAuth flow.
    window.location.assign('/oauth2/reauthenticate');
  }

  private handleIdleStart() : void {
    this.idleState = 'You\'ll be logged out in 15 seconds!';
    this.timedOut = true;

    Swal.fire({
      title: 'Session Timing Out',
      text: this.idleState,
      icon: 'info',
      showCancelButton: false,
      confirmButtonColor: '#4169E1',
      confirmButtonText: 'Keep me logged in!'
    }).then((result) => {
      if (result.value) {
        this.stay();
      }
    });

  }

  private handleTimeoutWarning(countdown : number) : void {
    this.idleState = `You will time out in ${countdown} seconds!`;
    Swal.update({ text: this.idleState});
  }

  @HostListener('window:beforeunload')
  ngOnDestroy(): void {
    // Tab close / navigation away — tear down subscriptions and stop the
    // idle watcher, but DO NOT trigger the timeout flow: handleTimeout
    // calls window.location.assign('/oauth2/authenticate') which would
    // hijack normal navigations. The HttpOnly cookies persist on the
    // browser side until they expire or the user re-authenticates.
    this.subs.unsubscribe();
    this.idle.stop();
  }

  public loading() : boolean {
    return this._loading;
  }

  /** Single source of truth for "user is staying logged in" — re-arms the
   *  ng-idle watcher and clears the timed-out flag. Called on idle-end
   *  (user becomes active again) and on the warning-dialog "Keep me logged
   *  in!" confirmation. (TASK-128 — drops the redundant reset() indirection.) */
  stay() {
    this.idle.watch();
    this.timedOut = false;
  }
}
